import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import {
  CalendarMonthOutlined,
  WavingHandOutlined,
  QuizOutlined,
  InsightsOutlined,
  RocketLaunchOutlined,
  AddOutlined
} from '../../icons';
import StudyPlanCalendar, { DAYS } from '../../components/StudyPlanCalendar/StudyPlanCalendar';
import { printLandscape } from '../../utils/printLandscape';
import { useCourse } from '../../contexts/CourseContext';
import './StudyPlan.css';

const GUIDED_INTRO_STEPS = [
  {
    icon: CalendarMonthOutlined,
    title: 'Monte seu plano de estudos',
    description: 'Escolha as matérias que vão cair na sua prova e diga a prioridade de cada uma.'
  },
  {
    icon: QuizOutlined,
    title: 'Responda 10 questões grátis',
    description: 'Preparamos 10 questões escolhidas com base exatamente no que você selecionou.'
  },
  {
    icon: InsightsOutlined,
    title: 'Veja seu resultado na hora',
    description: 'E descubra tudo o que a Preparate separou para te ajudar até o dia da prova.'
  }
];

const TRIAL_FLOW_STORAGE_KEY = 'trialFlowCompleted';

const apiUrl = process.env.REACT_APP_API_URL;

const PRIORITIES = ['alta', 'media', 'baixa'];
const PRIORITY_LABELS = { alta: 'Alta', media: 'Média', baixa: 'Baixa' };

function StudyPlan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeCourseId, activeCourse, loadingCourses } = useCourse();
  const guided = !!location.state?.guided;
  const [showGuidedIntro, setShowGuidedIntro] = useState(guided);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [mode, setMode] = useState('form');
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [newSubjectName, setNewSubjectName] = useState('');
  const [addingSubject, setAddingSubject] = useState(false);
  const [addSubjectError, setAddSubjectError] = useState('');

  const [hoursPerDay, setHoursPerDay] = useState(2);
  const [selectedDays, setSelectedDays] = useState([]);
  const [priorities, setPriorities] = useState({});

  useEffect(() => {
    // Espera o CourseContext resolver o concurso ativo a partir do banco
    // antes de decidir (não é mais salvo no localStorage, então não está
    // disponível de cara no primeiro render).
    if (loadingCourses) return;

    if (!activeCourseId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const [subjectsResponse, planResponse] = await Promise.all([
          axios.get(apiUrl + 'courseSubjects/' + activeCourseId),
          axios.get(apiUrl + 'studyPlan', { params: { courseId: activeCourseId } })
        ]);
        setSubjects(subjectsResponse.data.subjects || []);

        const existingPlan = planResponse.data.plan;
        if (existingPlan) {
          applyPlanToForm(existingPlan);
          setPlan(existingPlan);
          setMode('view');
        } else if (guided) {
          // Fluxo guiado (funil plano -> 10 questões -> venda) segue usando o
          // formulário local mesmo sem plano ainda — só o caminho normal
          // (usuário chegando sem passar pelo funil) é redirecionado para a
          // tela de seleção de matérias/tópicos.
          setPlan(null);
          setMode('form');
        } else {
          navigate('/selecionar-materias', { replace: true });
        }
      } catch (error) {
        console.error(error);
        alert('Erro ao carregar seus dados. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourseId, loadingCourses]);

  useEffect(() => {
    if (showGuidedIntro) {
      trackEvent('trial_flow_intro_shown');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissGuidedIntro = () => {
    trackEvent('trial_flow_intro_started');
    setShowGuidedIntro(false);
  };

  const applyPlanToForm = (existingPlan) => {
    setHoursPerDay(existingPlan.hoursPerDay);
    setSelectedDays(existingPlan.studyDays);
    const priorityBySubjectId = {};
    existingPlan.subjects.forEach((subject) => {
      priorityBySubjectId[subject.subjectId] = subject.priority;
    });
    setPriorities(priorityBySubjectId);
  };

  const toggleDay = (dayKey) => {
    setSelectedDays((prev) =>
      prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]
    );
  };

  const toggleSubject = (subjectId) => {
    setPriorities((prev) => {
      const updated = { ...prev };
      if (updated[subjectId]) {
        delete updated[subjectId];
      } else {
        updated[subjectId] = 'media';
      }
      return updated;
    });
  };

  const setSubjectPriority = (subjectId, priority) => {
    setPriorities((prev) => ({ ...prev, [subjectId]: priority }));
  };

  const handleAddCustomSubject = async (event) => {
    event.preventDefault();
    if (!newSubjectName.trim()) return;
    setAddingSubject(true);
    setAddSubjectError('');
    try {
      const response = await axios.post(apiUrl + `myCourses/${activeCourseId}/subjects`, {
        name: newSubjectName.trim()
      });
      setSubjects((prev) =>
        [...prev, { id: response.data.subjectId, name: newSubjectName.trim(), isCustom: true }].sort(
          (a, b) => a.name.localeCompare(b.name)
        )
      );
      setNewSubjectName('');
    } catch (error) {
      setAddSubjectError(error.response?.data?.message || 'Erro ao adicionar matéria.');
    } finally {
      setAddingSubject(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (selectedDays.length === 0) {
      setErrorMessage('Selecione ao menos um dia da semana.');
      return;
    }

    const chosenSubjects = Object.entries(priorities).map(([subjectId, priority]) => ({
      subjectId: Number(subjectId),
      priority
    }));

    if (chosenSubjects.length === 0) {
      setErrorMessage('Selecione ao menos uma matéria.');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(apiUrl + 'studyPlan/generate', {
        courseId: activeCourseId,
        hoursPerDay,
        studyDays: selectedDays,
        subjects: chosenSubjects
      });
      setPlan(response.data);

      if (guided) {
        trackEvent('trial_flow_plan_created', { subjectsCount: response.data.subjects.length });
        navigate('/questao-treino', {
          replace: true,
          state: { plan: response.data }
        });
        return;
      }

      setMode('view');
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error.response?.data?.message || 'Erro ao gerar o plano. Tente novamente.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Apagar o plano de estudos atual?')) return;
    try {
      await axios.delete(apiUrl + 'studyPlan', { params: { courseId: activeCourseId } });
      navigate('/selecionar-materias', { replace: true });
    } catch (error) {
      console.error(error);
      alert('Erro ao apagar o plano.');
    }
  };

  if (showGuidedIntro) {
    return (
      <div className="study-plan-page guided-intro-page">
        <div className="guided-intro-hero">
          <WavingHandOutlined className="guided-intro-hero-icon" style={{ fontSize: 44 }} />
          <h2 className="guided-intro-title">Vamos montar seu plano de estudos!</h2>
          <p className="guided-intro-subtitle">
            É rapidinho: em 3 passos você já sai daqui com um plano na mão e na prática.
          </p>
        </div>

        <div className="guided-intro-steps">
          {GUIDED_INTRO_STEPS.map((step, index) => (
            <div
              key={step.title}
              className="guided-intro-step"
              style={{ animationDelay: `${0.15 + index * 0.15}s` }}
            >
              <div className="guided-intro-step-number">{index + 1}</div>
              <step.icon className="guided-intro-step-icon" style={{ fontSize: 28 }} />
              <div className="guided-intro-step-text">
                <strong>{step.title}</strong>
                <span>{step.description}</span>
              </div>
            </div>
          ))}
        </div>

        <button type="button" className="guided-intro-cta" onClick={dismissGuidedIntro}>
          Vamos começar!
          <RocketLaunchOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 8 }} />
        </button>

        <button
          type="button"
          className="study-plan-skip-link guided-intro-skip"
          onClick={() => {
            trackEvent('trial_flow_skip');
            localStorage.setItem(TRIAL_FLOW_STORAGE_KEY, 'true');
            navigate('/simulados', { replace: true });
          }}
        >
          Pular por agora
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="study-plan-page">
        <div className="loading-message">Carregando...</div>
      </div>
    );
  }

  if (!activeCourseId) {
    return (
      <div className="study-plan-page">
        <div className="empty-message">
          Selecione um concurso na tela de simulados para montar seu plano de estudos.
        </div>
      </div>
    );
  }

  return (
    <div className={`study-plan-page ${mode === 'view' ? 'is-view' : ''}`}>
      {guided && (
        <button
          type="button"
          className="study-plan-skip-link"
          onClick={() => {
            trackEvent('trial_flow_skip');
            localStorage.setItem(TRIAL_FLOW_STORAGE_KEY, 'true');
            navigate('/simulados', { replace: true });
          }}
        >
          Pular por agora
        </button>
      )}

      <h2 className="study-plan-title">
        <CalendarMonthOutlined style={{ fontSize: 22, verticalAlign: 'middle', marginRight: 8 }} />
        Plano de Estudos Personalizado
      </h2>

      {mode === 'form' ? (
        <form className="study-plan-form" onSubmit={handleSubmit}>
          <div className="study-plan-field">
            <label>Quantas horas por dia você consegue estudar?</label>
            <input
              type="number"
              min="0.5"
              max="12"
              step="0.5"
              value={hoursPerDay}
              onChange={(e) => setHoursPerDay(Number(e.target.value))}
            />
          </div>

          <div className="study-plan-field">
            <label>Quais dias da semana pretende estudar?</label>
            <div className="study-plan-days">
              {DAYS.map((day) => (
                <button
                  type="button"
                  key={day.key}
                  className={`study-plan-day-chip ${selectedDays.includes(day.key) ? 'active' : ''}`}
                  onClick={() => toggleDay(day.key)}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div className="study-plan-field">
            <label>Quais matérias deseja estudar e qual a prioridade de cada uma?</label>
            <div className="study-plan-subjects">
              {subjects.map((subject) => {
                const isSelected = !!priorities[subject.id];
                return (
                  <div
                    key={subject.id}
                    className={`study-plan-subject-row ${isSelected ? 'active' : ''}`}
                  >
                    <label className="study-plan-subject-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSubject(subject.id)}
                      />
                      {subject.name}
                    </label>
                    {isSelected && (
                      <div className="study-plan-priority-selector">
                        {PRIORITIES.map((priority) => (
                          <button
                            type="button"
                            key={priority}
                            className={`study-plan-priority-btn ${priority} ${
                              priorities[subject.id] === priority ? 'active' : ''
                            }`}
                            onClick={() => setSubjectPriority(subject.id, priority)}
                          >
                            {PRIORITY_LABELS[priority]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {activeCourse?.isCustom && (
              <div className="study-plan-add-subject">
                <input
                  type="text"
                  value={newSubjectName}
                  onChange={(e) => setNewSubjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomSubject(e);
                    }
                  }}
                  placeholder="Nome da nova matéria"
                  maxLength={255}
                />
                <button type="button" onClick={handleAddCustomSubject} disabled={addingSubject || !newSubjectName.trim()}>
                  <AddOutlined style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                  {addingSubject ? 'Adicionando...' : 'Adicionar matéria'}
                </button>
                {addSubjectError && <span className="study-plan-add-subject-error">{addSubjectError}</span>}
              </div>
            )}
          </div>

          {errorMessage && <div className="study-plan-error">{errorMessage}</div>}

          <button type="submit" className="study-plan-submit" disabled={saving}>
            {saving ? 'Gerando...' : plan ? 'Gerar novamente' : 'Gerar plano de estudos'}
          </button>
        </form>
      ) : (
        <div className="study-plan-view">
          <div className="study-plan-view-actions">
            <button type="button" onClick={() => navigate('/selecionar-materias')}>
              Editar preferências
            </button>
            <button type="button" className="study-plan-print" onClick={printLandscape}>
              Imprimir plano
            </button>
            <button type="button" className="study-plan-reset" onClick={handleReset}>
              Apagar plano
            </button>
          </div>

          <h3 className="study-plan-print-title">Plano de Estudos Personalizado</h3>

          <StudyPlanCalendar plan={plan} />
        </div>
      )}
    </div>
  );
}

export default StudyPlan;
